import React, { useState, useEffect } from 'react';
import DeployHome from '../components/deploy/DeployHome';
import ZipDeploy from '../components/deploy/ZipDeploy';
import DockerImageDeploy from '../components/deploy/DockerImageDeploy';
import ComposeDeploy from '../components/deploy/ComposeDeploy';
import TemplateDeploy from '../components/deploy/TemplateDeploy';
import DockerHubDeploy from '../components/deploy/DockerHubDeploy';
import GitRepoDeploy from '../components/deploy/GitRepoDeploy';
import AIDeploy from '../components/deploy/AIDeploy';
import ComposeWorkspaceModal from '../components/ComposeWorkspaceModal';
import api from '../services/api';
import '../styles/dashboard.css';

export default function Deploy({ user, initialTab = null, onStatusMessage, onTabChange, openGlobalWorkspace }) {
  const [activeTab, setActiveTab] = useState(initialTab || null);
  const [deploymentSessionId, setDeploymentSessionId] = useState(null);
  const [deploymentSession, setDeploymentSession] = useState(null);
  const [, setShowArchived] = useState(true);
  const [workspaceMinimized, setWorkspaceMinimized] = useState(false);
  const [composeWorkspaceApp, setComposeWorkspaceApp] = useState(null);
  const [composeWorkspaceInfo, setComposeWorkspaceInfo] = useState(null);
  useEffect(() => {
    setActiveTab(initialTab || null);
  }, [initialTab]);

  useEffect(() => {
    const restoreSession = async () => {
      try {
        const { activeDeployments } = await api.getStatus();
        if (activeDeployments && activeDeployments.length > 0) {
          // Find the most recent running session (only if actively running)
          const runningSession = activeDeployments.find(s => s.status === 'running');
          if (!runningSession) return;
          
          setDeploymentSessionId(runningSession.id);
          setDeploymentSession(runningSession);
          
          const t = runningSession.meta?.type;
          setActiveTab(prev => {
            if (prev !== null) return prev; // User already made a selection
            if (t === 'zip') return 'zip';
            if (t === 'docker') return 'docker';
            if (t === 'compose') return 'compose';
            if (t === 'hub') return 'hub';
            if (t === 'repo' || t === 'ssr' || t === 'backend-workspace') return 'repo-deploying';
            return prev;
          });
          // Add others as needed
        }
      } catch (err) {
        console.error('Failed to restore active deployments', err);
      }
    };
    if (!initialTab && !activeTab) {
      restoreSession();
    }
  }, []);

  useEffect(() => {
    const handleSwitch = () => {
      setActiveTab(null);
      setDeploymentSessionId(null);
      setDeploymentSession(null);
    };
    window.addEventListener('switch-to-dashboard', handleSwitch);
    return () => window.removeEventListener('switch-to-dashboard', handleSwitch);
  }, []);

  const openComposeWorkspace = (appName, info) => {
    // Keep local state just in case, but rely on global state primarily
    setComposeWorkspaceApp(appName);
    setComposeWorkspaceInfo(info || null);
    setWorkspaceMinimized(false);
    
    if (openGlobalWorkspace) {
      openGlobalWorkspace({
        appName,
        sshInfo: info || null,
        deploymentSession,
        deploymentSessionId,
        deploymentType: info?.deploymentType || 'compose',
        initialTab: info?.initialTab || 'ai',
        autoDeploy: info?.autoDeploy === true
      });
    }
  };
  const minimizeWorkspace = () => {
    setWorkspaceMinimized(true);
  };
  const restoreWorkspace = () => {
    setWorkspaceMinimized(false);
  };
  const closeComposeWorkspace = (options = {}) => {
    setComposeWorkspaceApp(null);
    setComposeWorkspaceInfo(null);
    setWorkspaceMinimized(false);

    if (options?.cancelled || deploymentSession?.status === 'cancelled') {
      setActiveTab(null);
      setDeploymentSessionId(null);
      setDeploymentSession(null);
      return;
    }

    // After a verified deployment, stay on the success page — do not jump to dashboard.
    if (options?.verified) {
      return;
    }

    window.dispatchEvent(new CustomEvent('switch-to-dashboard'));
  };

  const createDeploymentId = () => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return `deploy-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  };

  const beginDeploymentTracking = (type, currentAppName, customId = null) => {
    const deploymentId = customId || createDeploymentId();
    setShowArchived(false);

    const milestoneTemplates = {
      'alma-linux': [
        { key: 'fetching', label: 'Pulling Alma Linux image...', status: 'running', message: 'Starting...' },
        { key: 'checking', label: 'Creating isolated environment...', status: 'pending', message: '' },
        { key: 'building', label: 'Starting SSH daemon...', status: 'pending', message: '' },
        { key: 'deploying', label: 'Configuring network routing...', status: 'pending', message: '' },
        { key: 'finalizing', label: 'Finalizing container setup...', status: 'pending', message: '' }
      ],
      repo: [
        { key: 'fetching', label: 'Fetching repository', status: 'running', message: 'Starting...' },
        { key: 'checking', label: 'Checking repository', status: 'pending', message: '' },
        { key: 'building', label: 'Building application', status: 'pending', message: '' },
        { key: 'deploying', label: 'Deploying to gateway', status: 'pending', message: '' }
      ],
      zip: [
        { key: 'upload_received', label: 'Upload received', status: 'running', message: '' },
        { key: 'validating', label: 'Validating package', status: 'pending', message: '' },
        { key: 'allocating_port', label: 'Allocating port', status: 'pending', message: '' },
        { key: 'extracting_files', label: 'Extracting files', status: 'pending', message: '' },
        { key: 'configuring_gateway', label: 'Configuring gateway', status: 'pending', message: '' }
      ],
      'backend-workspace': [
        { key: 'fetching', label: 'Fetching repository', status: 'running', message: 'Starting...' },
        { key: 'checking', label: 'Preparing workspace files', status: 'pending', message: '' },
        { key: 'building', label: 'Starting workspace container', status: 'pending', message: '' },
        { key: 'deploying', label: 'Configuring public route', status: 'pending', message: '' }
      ],
      ssr: [
        { key: 'fetching', label: 'Fetching repository', status: 'running', message: 'Starting...' },
        { key: 'checking', label: 'Preparing SSR workspace', status: 'pending', message: '' },
        { key: 'building', label: 'Starting workspace container', status: 'pending', message: '' },
        { key: 'deploying', label: 'Configuring public route', status: 'pending', message: '' }
      ],
      compose: [
        { key: 'validating', label: 'Initialization started', status: 'running', message: '' },
        { key: 'allocating_port', label: 'Allocating port', status: 'pending', message: '' },
        { key: 'extracting_files', label: 'Loading Template Image', status: 'pending', message: '' },
        { key: 'preparing_runtime', label: 'Preparing runtime', status: 'pending', message: '' },
        { key: 'starting_runtime', label: 'Starting Workspace Container', status: 'pending', message: '' },
        { key: 'configuring_gateway', label: 'Configuring gateway', status: 'pending', message: '' }
      ],
      docker: [
        { key: 'upload_received', label: 'Upload received', status: 'running', message: '' },
        { key: 'validating', label: 'Validating package', status: 'pending', message: '' },
        { key: 'allocating_port', label: 'Allocating port', status: 'pending', message: '' },
        { key: 'loading_image', label: 'Loading image', status: 'pending', message: '' },
        { key: 'starting_runtime', label: 'Starting container', status: 'pending', message: '' },
        { key: 'configuring_gateway', label: 'Configuring gateway', status: 'pending', message: '' }
      ],
      hub: [
        { key: 'upload_received', label: 'Upload received', status: 'running', message: '' },
        { key: 'validating', label: 'Validating package', status: 'pending', message: '' },
        { key: 'allocating_port', label: 'Allocating port', status: 'pending', message: '' },
        { key: 'loading_image', label: 'Loading image', status: 'pending', message: '' },
        { key: 'starting_runtime', label: 'Starting container', status: 'pending', message: '' },
        { key: 'configuring_gateway', label: 'Configuring gateway', status: 'pending', message: '' }
      ]
    };

    setDeploymentSessionId(deploymentId);
    setDeploymentSession({
      status: 'running',
      logs: [{ ts: new Date().toISOString(), level: 'info', message: 'Initializing deployment...' }],
      milestones: milestoneTemplates[type] || [],
      meta: { type, appName: currentAppName },
      uploadProgress: 0
    });

    return deploymentId;
  };

  const refreshDeploymentSession = async (deploymentId) => {
    try {
      const session = await api.getDeploymentStatus(deploymentId);
      setDeploymentSession(session);
      return session;
    } catch (_) {
      return null;
    }
  };

  const setCurrentSessionProgress = (percent) => {
    setDeploymentSession(prev => prev ? ({ ...prev, uploadProgress: percent }) : prev);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (onTabChange) {
      onTabChange(tab);
    }
  };

  const commonProps = {
    user,
    onStatusMessage,
    onTabChange: handleTabChange,
    api,
    beginDeploymentTracking,
    refreshDeploymentSession,
    setCurrentSessionProgress,
    openComposeWorkspace,
    closeComposeWorkspace,
    minimizeWorkspace,
    restoreWorkspace,
    workspaceMinimized,
    deploymentSessionId,
    deploymentSession,
    setActiveTab: handleTabChange,
    setDeploymentSessionId,
    onBack: () => handleTabChange(null)
  };

  return (
    <>
      <div className="dashboard-content animate-fade-in vercel-dashboard-container">
        {activeTab === null && <DeployHome setActiveTab={handleTabChange} />}
        {activeTab === 'zip' && <ZipDeploy {...commonProps} />}
        {activeTab === 'docker' && <DockerImageDeploy {...commonProps} />}
        {activeTab === 'compose' && <ComposeDeploy {...commonProps} />}
        {activeTab === 'template' && <TemplateDeploy {...commonProps} />}
        {activeTab === 'hub' && <DockerHubDeploy {...commonProps} />}
        {(activeTab === 'repo' || activeTab === 'repo-configure' || activeTab === 'repo-deploying') && (
          <GitRepoDeploy {...commonProps} activeTab={activeTab} setActiveTab={handleTabChange} />
        )}
        {activeTab === 'ai-deploy' && <AIDeploy {...commonProps} />}
        {activeTab != null
          && activeTab !== 'zip'
          && activeTab !== 'docker'
          && activeTab !== 'compose'
          && activeTab !== 'template'
          && activeTab !== 'hub'
          && activeTab !== 'repo'
          && activeTab !== 'repo-configure'
          && activeTab !== 'repo-deploying'
          && activeTab !== 'ai-deploy'
          && <DeployHome setActiveTab={handleTabChange} />}
      </div>
    </>
  );
}
